import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, CheckCircle2Icon, CheckIcon, Clock3Icon, FileDownIcon } from 'lucide-react';
import { Button } from './Button';
import { Checkbox } from './Checkbox';
import { Modal } from './Modal';
import { type RunningOrderSummary } from './runningOrderOfficeData';
type DetailTab = 'status' | 'materials' | 'logbook';
type RunningOrderDetailProps = {
  order: RunningOrderSummary;
  onBack: () => void;
};
type AuditEntry = {
  id: string;
  timestamp: string;
  action: string;
  actor: string;
  tone: 'default' | 'adjusted';
};
const detailTabs: {
  id: DetailTab;
  label: string;
}[] = [{
  id: 'status',
  label: 'Auftragsstatus'
}, {
  id: 'materials',
  label: 'Rohstoffe'
}, {
  id: 'logbook',
  label: 'Live-Logbuch'
}];
export function RunningOrderDetail({
  order,
  onBack
}: RunningOrderDetailProps) {
  const [activeTab, setActiveTab] = useState<DetailTab>('status');
  const currentLot = Math.min(order.completedLots + 1, order.lots);
  const [selectedLot, setSelectedLot] = useState(currentLot);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [includeMaterials, setIncludeMaterials] = useState(true);
  const [includeLogbook, setIncludeLogbook] = useState(true);
  const [isExportSuccessVisible, setIsExportSuccessVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const progress = Math.round(order.completedLots / order.lots * 100);
  const auditEntries = createMockAuditEntries(order);
  useEffect(() => {
    const loadingTimeoutId = window.setTimeout(() => setIsLoading(false), 2000);
    return () => window.clearTimeout(loadingTimeoutId);
  }, []);
  useEffect(() => {
    if (!isExportSuccessVisible) {
      return;
    }
    const timeoutId = window.setTimeout(() => setIsExportSuccessVisible(false), 3500);
    return () => window.clearTimeout(timeoutId);
  }, [isExportSuccessVisible]);
  function handleExport() {
    setIsExportOpen(false);
    setIsExportSuccessVisible(true);
  }
  function handleTabKeyDown(event: React.KeyboardEvent<HTMLButtonElement>, currentIndex: number) {
    const isNextKey = event.key === 'ArrowRight' || event.key === 'ArrowDown';
    const isPreviousKey = event.key === 'ArrowLeft' || event.key === 'ArrowUp';
    if (!isNextKey && !isPreviousKey && event.key !== 'Home' && event.key !== 'End') {
      return;
    }
    event.preventDefault();
    const nextIndex = event.key === 'Home' ? 0 : event.key === 'End' ? detailTabs.length - 1 : (currentIndex + (isNextKey ? 1 : -1) + detailTabs.length) % detailTabs.length;
    const nextTab = detailTabs[nextIndex];
    setActiveTab(nextTab.id);
    window.requestAnimationFrame(() => {
      document.getElementById(`${order.id}-${nextTab.id}-tab`)?.focus();
    });
  }
  return <section className="min-h-screen w-full bg-surface-subtle font-body text-text-primary" data-id="element-835">
      <motion.header className="sticky top-0 z-10 border-b border-border bg-surface" initial={{
      opacity: 0,
      y: -8
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.28,
      ease: 'easeOut'
    }} data-id="element-836">
        <div className="flex items-start justify-between gap-4 border-b border-border px-5 py-4 sm:px-8 lg:px-10" data-id="element-837">
          <div className="flex min-w-0 items-start gap-3" data-id="element-838">
            <button type="button" onClick={onBack} className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-border bg-surface text-text-primary transition-colors hover:bg-brand-yellow-light focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-brand-green" aria-label="Zurück zur Fortschrittsmatrix" data-id="element-839">
              <ArrowLeftIcon size={19} strokeWidth={2} aria-hidden="true" data-id="element-840" />
            </button>
            <div className="min-w-0" data-id="element-841">
              <p className="text-xs font-semibold uppercase tracking-[0.12em] text-text-secondary" data-id="element-842">
                Produktionsprotokoll
              </p>
              <h1 className="mt-1 truncate font-heading text-2xl sm:text-3xl" data-id="element-843">
                {order.id}
              </h1>
            </div>
          </div>
          <Button variant="secondary" size="sm" onClick={() => setIsExportOpen(true)} leadingIcon={<FileDownIcon size={16} aria-hidden="true" data-id="element-845" />} data-id="element-844">
            PDF exportieren
          </Button>
        </div>

        <dl className="grid grid-cols-2 gap-x-6 gap-y-5 px-5 py-5 sm:grid-cols-4 sm:px-8 lg:px-10" data-id="element-846">
          <MetadataItem label="Auftrag" value={order.id} detail={order.product} data-id="element-847" />
          <MetadataItem label="Lot" value={`${currentLot} von ${order.lots}`} detail={`${progress} % abgeschlossen`} data-id="element-848" />
          <MetadataItem label="Bearbeiter" value={order.operator} detail={`Gestartet ${order.startedAt}`} data-id="element-849" />
          <MetadataItem label="In Produktion" value={order.productionDuration} data-id="element-850" />
        </dl>

        <div className="flex overflow-x-auto px-5 sm:px-8 lg:px-10" role="tablist" aria-label="Auftragsdetails" data-id="element-851">
          {detailTabs.map((tab, index) => {
          const isActive = activeTab === tab.id;
          return <button key={tab.id} id={`${order.id}-${tab.id}-tab`} type="button" role="tab" aria-selected={isActive} aria-controls={`${order.id}-${tab.id}-panel`} tabIndex={isActive ? 0 : -1} onClick={() => setActiveTab(tab.id)} onKeyDown={event => handleTabKeyDown(event, index)} className={`shrink-0 border-b-2 px-4 py-3 text-xs font-semibold uppercase tracking-[0.12em] transition-colors focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-[-4px] focus-visible:outline-brand-green ${isActive ? 'border-brand-green text-text-primary' : 'border-transparent text-text-secondary hover:text-text-primary'}`} data-id="element-852">
                {tab.label}
              </button>;
        })}
        </div>
      </motion.header>

      <div className="mx-auto w-full max-w-[1540px] px-5 py-6 sm:px-8 sm:py-8 lg:px-10" data-id="element-853">
        {isLoading ? <RunningOrderDetailSkeleton data-id="element-854" /> : <>
            {activeTab === 'status' ? <motion.div key="status" id={`${order.id}-status-panel`} role="tabpanel" aria-labelledby={`${order.id}-status-tab`} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.24,
          ease: 'easeOut'
        }} data-id="element-855">
                <MaterialsSection order={order} selectedLot={selectedLot} onSelectLot={setSelectedLot} data-id="element-856" />
                <AuditSection entries={auditEntries} data-id="element-857" />
              </motion.div> : null}

            {activeTab === 'materials' ? <motion.div key="materials" id={`${order.id}-materials-panel`} role="tabpanel" aria-labelledby={`${order.id}-materials-tab`} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.24,
          ease: 'easeOut'
        }} data-id="element-858">
                <MaterialsSection order={order} selectedLot={selectedLot} onSelectLot={setSelectedLot} data-id="element-859" />
              </motion.div> : null}

            {activeTab === 'logbook' ? <motion.div key="logbook" id={`${order.id}-logbook-panel`} role="tabpanel" aria-labelledby={`${order.id}-logbook-tab`} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.24,
          ease: 'easeOut'
        }} data-id="element-860">
                <AuditSection entries={auditEntries} data-id="element-861" />
              </motion.div> : null}
          </>}
      </div>

      <Modal open={isExportOpen} onOpenChange={setIsExportOpen} title="PDF exportieren" description="Auftragsdaten werden immer in den Export aufgenommen. Wählen Sie die zusätzlichen Bereiche aus." size="sm" closeLabel="Exportdialog schließen" footer={<>
            <Button variant="secondary" onClick={() => setIsExportOpen(false)} data-id="element-863">
              Abbrechen
            </Button>
            <Button onClick={handleExport} leadingIcon={<FileDownIcon size={16} aria-hidden="true" data-id="element-865" />} data-id="element-864">
              Exportieren
            </Button>
          </>} data-id="element-862">
        <div className="space-y-4" data-id="element-866">
          <div className="rounded-md border border-border bg-surface-subtle p-4" data-id="element-867">
            <p className="font-heading text-sm text-text-primary" data-id="element-868">
              Auftragsdaten
            </p>
            <p className="mt-1 text-sm text-text-secondary" data-id="element-869">
              Immer enthalten: Auftrag, Lot, Fortschritt, Bearbeiter und
              Produktionsdauer.
            </p>
          </div>
          <fieldset className="space-y-3" data-id="element-870">
            <legend className="text-xs font-semibold uppercase tracking-[0.12em] text-text-secondary" data-id="element-871">
              Zusätzliche Inhalte
            </legend>
            <Checkbox id={`${order.id}-export-materials`} label="Rohstoffe" description="Lotbezogene Soll- und verwogene Mengen aufnehmen." checked={includeMaterials} onCheckedChange={setIncludeMaterials} data-id="element-872" />
            <Checkbox id={`${order.id}-export-logbook`} label="Live-Logbuch" description="Chronologisches Produktionsprotokoll aufnehmen." checked={includeLogbook} onCheckedChange={setIncludeLogbook} data-id="element-873" />
          </fieldset>
        </div>
      </Modal>

      {isExportSuccessVisible ? <motion.div role="status" aria-live="polite" className="fixed right-5 top-5 z-30 flex max-w-sm items-start gap-3 rounded-lg border border-success bg-surface p-4 shadow-md sm:right-8 sm:top-8" initial={{
      opacity: 0,
      y: -10
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: -10
    }} transition={{
      duration: 0.22,
      ease: 'easeOut'
    }} data-id="element-874">
          <CheckCircle2Icon size={20} className="mt-0.5 shrink-0 text-success" aria-hidden="true" data-id="element-875" />
          <div data-id="element-876">
            <p className="font-heading text-sm text-text-primary" data-id="element-877">
              Erfolgreich exportiert
            </p>
            <p className="mt-1 text-xs leading-5 text-text-secondary" data-id="element-878">
              Die ausgewählten Inhalte für {order.id} wurden vorbereitet.
            </p>
          </div>
        </motion.div> : null}
    </section>;
}
function RunningOrderDetailSkeleton() {
  return <div className="animate-pulse" aria-busy="true" aria-label="Produktionsprotokoll wird geladen" data-id="element-879">
      <div className="flex items-end justify-between" data-id="element-880">
        <div data-id="element-881">
          <div className="h-3 w-36 rounded bg-border" data-id="element-882" />
          <div className="mt-3 h-3 w-64 rounded bg-border" data-id="element-883" />
        </div>
        <div className="h-4 w-24 rounded bg-border" data-id="element-884" />
      </div>

      <div className="mt-4 flex gap-2 overflow-hidden" data-id="element-885">
        {Array.from({
        length: 6
      }, (_, index) => <div key={index} className="h-14 w-24 shrink-0 rounded-md bg-border" data-id="element-886" />)}
      </div>

      <div className="mt-4 overflow-hidden rounded-lg border border-border bg-surface" data-id="element-887">
        <div className="h-11 bg-surface-subtle" data-id="element-888" />
        {Array.from({
        length: 3
      }, (_, index) => <div key={index} className="grid grid-cols-5 gap-4 border-t border-border px-4 py-5" data-id="element-889">
              {Array.from({
          length: 5
        }, (_, cellIndex) => <div key={cellIndex} className="h-4 rounded bg-border" style={{
          width: `${64 + (index + cellIndex) % 3 * 12}%`
        }} data-id="element-890" />)}
            </div>)}
      </div>

      <div className="mt-8" data-id="element-891">
        <div className="h-3 w-28 rounded bg-border" data-id="element-892" />
        <div className="mt-4 space-y-3" data-id="element-893">
          {Array.from({
          length: 3
        }, (_, index) => <div key={index} className="grid gap-3 rounded-md border-l-[3px] border-border bg-surface p-4 sm:grid-cols-[136px_minmax(0,1fr)] sm:gap-5" data-id="element-894">
                <div className="h-3 w-28 rounded bg-border" data-id="element-895" />
                <div data-id="element-896">
                  <div className="h-4 w-2/3 rounded bg-border" data-id="element-897" />
                  <div className="mt-3 h-3 w-24 rounded bg-border" data-id="element-898" />
                </div>
              </div>)}
        </div>
      </div>
    </div>;
}
type MetadataItemProps = {
  label: string;
  value: string;
  detail?: string;
};
function MetadataItem({
  label,
  value,
  detail
}: MetadataItemProps) {
  return <div data-id="element-899">
      <dt className="text-[10px] font-semibold uppercase tracking-[0.12em] text-text-secondary" data-id="element-900">
        {label}
      </dt>
      <dd className="mt-2 font-heading text-sm text-text-primary" data-id="element-901">{value}</dd>
      {detail ? <p className="mt-1 text-xs leading-5 text-text-secondary" data-id="element-902">{detail}</p> : null}
    </div>;
}
type MaterialsSectionProps = {
  order: RunningOrderSummary;
  selectedLot: number;
  onSelectLot: (lot: number) => void;
};
function MaterialsSection({
  order,
  selectedLot,
  onSelectLot
}: MaterialsSectionProps) {
  const currentLot = Math.min(order.completedLots + 1, order.lots);
  const lots = Array.from({
    length: order.lots
  }, (_, index) => index + 1);
  return <section aria-labelledby="materials-heading" data-id="element-903">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between" data-id="element-904">
        <div data-id="element-905">
          <h2 id="materials-heading" className="text-xs font-semibold uppercase tracking-[0.12em] text-text-secondary" data-id="element-906">
            Rohstoffe nach Lot
          </h2>
          <p className="mt-1 text-xs text-text-secondary" data-id="element-907">
            Lot auswählen, um Soll- und verwogene Mengen zu prüfen.
          </p>
        </div>
        <p className="text-sm font-semibold text-text-primary" data-id="element-908">
          Lot {selectedLot} von {order.lots}
        </p>
      </div>

      <ol className="mt-4 flex gap-2 overflow-x-auto pb-1" aria-label="Lots des Auftrags" data-id="element-909">
        {lots.map(lot => {
        const isSelected = lot === selectedLot;
        const status = lot <= order.completedLots ? 'abgeschlossen' : lot === currentLot ? 'aktuell' : 'offen';
        return <li key={lot} data-id="element-910">
              <button type="button" onClick={() => onSelectLot(lot)} aria-pressed={isSelected} className={`min-w-[92px] rounded-md border px-3 py-2 text-left text-xs transition-colors focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-brand-green ${isSelected ? 'border-brand-green bg-brand-yellow-light text-text-primary' : 'border-border bg-surface text-text-secondary hover:bg-surface-subtle hover:text-text-primary'}`} data-id="element-911">
                <span className="block font-heading text-sm" data-id="element-912">Lot {lot}</span>
                <span className="mt-0.5 block" data-id="element-913">{status}</span>
              </button>
            </li>;
      })}
      </ol>

      <div className="mt-4 overflow-x-auto rounded-lg border border-border bg-surface shadow-sm" data-id="element-914">
        <table className="w-full min-w-[840px] text-left" data-id="element-915">
          <caption className="sr-only" data-id="element-916">
            Read-only Rohstoffprüfung für Lot {selectedLot} mit Rohstoffcharge,
            Sollmenge, verwogener Menge und Status.
          </caption>
          <thead className="border-b border-border bg-surface-subtle text-[10px] font-semibold uppercase tracking-[0.12em] text-text-secondary" data-id="element-917">
            <tr data-id="element-918">
              <th scope="col" className="px-4 py-3" data-id="element-919">
                Rohstoff-Code
              </th>
              <th scope="col" className="px-4 py-3" data-id="element-920">
                Bezeichnung
              </th>
              <th scope="col" className="px-4 py-3" data-id="element-921">
                Rohstoffcharge
              </th>
              <th scope="col" className="px-4 py-3 text-right" data-id="element-922">
                Sollmenge
              </th>
              <th scope="col" className="px-4 py-3 text-right" data-id="element-923">
                Verwogen
              </th>
              <th scope="col" className="px-4 py-3" data-id="element-924">
                Status
              </th>
            </tr>
          </thead>
          <tbody data-id="element-925">
            {order.materials.map(material => {
            const isWeighed = selectedLot <= order.completedLots || selectedLot === currentLot && Boolean(material.isWeighed);
            return <tr key={material.articleNumber} className="border-b border-border last:border-b-0" data-id="element-926">
                  <th scope="row" className="whitespace-nowrap px-4 py-4 font-heading text-sm" data-id="element-927">
                    {material.articleNumber}
                  </th>
                  <td className="px-4 py-4 text-sm text-text-secondary" data-id="element-928">
                    {material.description}
                  </td>
                  <td className="whitespace-nowrap px-4 py-4 font-heading text-sm text-text-primary" data-id="element-929">
                    {getRawMaterialBatchNumber(material.articleNumber, selectedLot)}
                  </td>
                  <td className="whitespace-nowrap px-4 py-4 text-right font-heading text-sm" data-id="element-930">
                    {formatQuantity(material.quantityPerLotKg)} kg
                  </td>
                  <td className="whitespace-nowrap px-4 py-4 text-right font-heading text-sm" data-id="element-931">
                    {isWeighed ? <span className="text-success" data-id="element-932">
                        {formatQuantity(material.quantityPerLotKg)} kg
                      </span> : <span className="text-text-secondary" aria-label="Noch nicht verwogen" data-id="element-933">
                        —
                      </span>}
                  </td>
                  <td className="px-4 py-4" data-id="element-934">
                    {isWeighed ? <span className="inline-flex items-center gap-2 rounded-md bg-success/15 px-2.5 py-1.5 text-xs font-semibold text-success" data-id="element-935">
                        <CheckIcon size={14} strokeWidth={2.8} aria-hidden="true" data-id="element-936" />
                        Verwogen
                      </span> : <span className="inline-flex items-center gap-2 text-xs font-semibold text-text-secondary" data-id="element-937">
                        <Clock3Icon size={14} strokeWidth={2} aria-hidden="true" data-id="element-938" />
                        Ausstehend
                      </span>}
                  </td>
                </tr>;
          })}
          </tbody>
        </table>
      </div>
    </section>;
}
type AuditSectionProps = {
  entries: AuditEntry[];
};
function AuditSection({
  entries
}: AuditSectionProps) {
  return <section className="mt-8" aria-labelledby="audit-heading" data-id="element-939">
      <div className="flex flex-wrap items-baseline justify-between gap-2" data-id="element-940">
        <div data-id="element-941">
          <h2 id="audit-heading" className="text-xs font-semibold uppercase tracking-[0.12em] text-text-secondary" data-id="element-942">
            Live-Logbuch
          </h2>
          <p className="mt-1 text-xs text-text-secondary" data-id="element-943">
            Mock-Dokumentation für die Office-Ansicht
          </p>
        </div>
      </div>

      <ol className="mt-4 space-y-3" aria-label="Chronologisches Auditprotokoll" data-id="element-944">
        {entries.map((entry, index) => <motion.li key={entry.id} className={`grid gap-3 rounded-md border-l-[3px] bg-surface p-4 shadow-sm sm:grid-cols-[136px_minmax(0,1fr)] sm:gap-5 ${entry.tone === 'adjusted' ? 'border-error' : 'border-brand-green'}`} initial={{
        opacity: 0,
        x: -12
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        duration: 0.28,
        delay: index * 0.07,
        ease: 'easeOut'
      }} data-id="element-945">
            <time dateTime="2026-07-13" className="text-xs font-semibold leading-5 text-text-secondary" data-id="element-946">
              {entry.timestamp}
            </time>
            <div className="min-w-0" data-id="element-947">
              <p className={`font-heading text-sm ${entry.tone === 'adjusted' ? 'text-error' : 'text-text-primary'}`} data-id="element-948">
                {entry.action}
              </p>
              <p className="mt-1 text-xs text-text-secondary" data-id="element-949">{entry.actor}</p>
            </div>
          </motion.li>)}
      </ol>
    </section>;
}
function createMockAuditEntries(order: RunningOrderSummary): AuditEntry[] {
  const weighedMaterial = order.materials.find(material => material.isWeighed);
  const entries: AuditEntry[] = [{
    id: `${order.id}-weighing-completed`,
    timestamp: '13.07.2026 · 09:40 Uhr',
    action: weighedMaterial ? `Wiegung abgeschlossen · ${weighedMaterial.articleNumber} · ${weighedMaterial.description} · ${formatQuantity(weighedMaterial.quantityPerLotKg)} kg` : 'Wiegung abgeschlossen',
    actor: order.operator,
    tone: 'default'
  }, {
    id: `${order.id}-weighing-started`,
    timestamp: '13.07.2026 · 09:38 Uhr',
    action: 'Wiegung ausgelöst',
    actor: order.operator,
    tone: 'default'
  }, {
    id: `${order.id}-material-scanned`,
    timestamp: '13.07.2026 · 09:37 Uhr',
    action: weighedMaterial ? `Rohstoff gescannt · ${weighedMaterial.articleNumber} · ${weighedMaterial.description}` : 'Rohstoff gescannt',
    actor: order.operator,
    tone: 'default'
  }];
  if (order.supervisorNote === 'Rezeptur angepasst') {
    entries.push({
      id: `${order.id}-recipe-adjusted`,
      timestamp: '13.07.2026 · 09:35 Uhr',
      action: 'Rezeptur angepasst und freigegeben',
      actor: 'Supervisor M. Schmidt',
      tone: 'adjusted'
    });
  } else {
    entries.push({
      id: `${order.id}-recipe-approved`,
      timestamp: '13.07.2026 · 09:35 Uhr',
      action: 'Rezept freigegeben',
      actor: 'Supervisor M. Schmidt',
      tone: 'default'
    });
  }
  entries.push({
    id: `${order.id}-started`,
    timestamp: '13.07.2026 · 09:30 Uhr',
    action: 'Auftrag gestartet',
    actor: order.operator,
    tone: 'default'
  }, {
    id: `${order.id}-created`,
    timestamp: order.startedAt,
    action: 'Auftrag erstellt',
    actor: 'System',
    tone: 'default'
  });
  return entries;
}
function getRawMaterialBatchNumber(articleNumber: string, productionLot: number) {
  const materialCode = articleNumber.replace(/[^A-Z0-9]/g, '');
  return `RMCH-2607${String(productionLot).padStart(2, '0')}-${materialCode.slice(-4)}`;
}
function formatQuantity(quantity: number) {
  return quantity.toLocaleString('de-DE', {
    maximumFractionDigits: 1
  });
}